import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import postal from 'postal';

import Notification from './notification';

export default class NotificationItem extends React.Component {
  static propTypes = {
    notification: PropTypes.instanceOf(Notification).isRequired,
    anonymousNotificationsEnabled: PropTypes.bool.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      read: !!props.notification.isRead(),
      anonymousNotificationsEnabled: props.anonymousNotificationsEnabled,
    };
    this.$containerNotificationsCalloutToggle = null;
    this.$container = null;
    this.onClick = this.onClick.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onStateChange = this.onStateChange.bind(this);

    this.postal = postal.channel('notifications.commands');
  }

  componentDidMount() {
    this.subscription = postal.subscribe({
      channel: 'notifications.commands',
      topic: 'read-status',
      callback: this.onStateChange.bind(this),
    });
  }

  componentWillUnmount() {
    this.subscription.unsubscribe();
  }

  onToggle(event) {
    event && event.stopPropagation();
    this.props.notification.toggleReadStatus();
  }

  onClick(event) {
    event && event.preventDefault();
    this.props.notification.open();
  }

  onStateChange(data) {
    if (_.get(data, 'id') === this.props.notification.id) {
      this.setState({ read: _.get(data, 'isRead') });
    }
  }

  get profileName() {
    const n = this.props.notification;

    if (_.get(n, 'profile.id') === _.get(window, 'Livewire.currentUser.profile.id')) {
      return 'You';
    }

    return _.get(n, 'profile.name')
      || _.get(n, 'rolled_up_profiles[0].name')
      || 'Somebody';
  }

  get profilePictureForLikeComment() {
    const n = this.props.notification;
    const likedByAuthor = _.get(n, 'like.liked_by_author', false);

    const authorLikeAvatar = _.get(n, 'profile.picture_url')
      || _.get(n, 'rolled_up_profiles[0].picture_url')
      || window.getLivewireAssetPath('avatar-fallback.png');
    const avatarAlt = this.state.anonymousNotificationsEnabled
      ? 'profile avatar'
      : this.profileName;
    const avatarSrc = this.state.anonymousNotificationsEnabled
      ? window.getLivewireAssetPath('avatar-fallback.png')
      : authorLikeAvatar;

    const avatar = likedByAuthor ? (
      <img
        alt={avatarAlt}
        src={avatarSrc}
      />
    ) : <div className="like-comment" />;

    const likeIcon = likedByAuthor ? (
      <div className="icon-lw icon-lw-contributor-liked">
        <span className="path1" />
        <span className="path2" />
      </div>
    ) : (
      <div className="like-comment-icon">
        <span className="icon-lw icon-lw-comment-active" />
        <span className="icon-lw icon-lw-wire-liked" />
        <span className="icon-lw icon-lw-wire-like" />
      </div>
    );

    return (
      <>
        {avatar}
        {likeIcon}
      </>
    );
  }

  get profilePicture() {
    const n = this.props.notification;
    const { type } = n;

    if (type === 'alert_comment_published') {
      return null;
    }

    if (type === 'liked_comment') {
      return this.profilePictureForLikeComment;
    }

    if (this.state.anonymousNotificationsEnabled && ['liked_wire', 'contributor_alert'].includes(type)) {
      return (
        <img
          alt="profile avatar"
          src={window.getLivewireAssetPath('avatar-fallback.png')}
        />
      );
    }

    return (
      <img
        alt={this.profileName}
        src={_.get(n, 'profile.picture_url')
          || _.get(n, 'rolled_up_profiles[0].picture_url')
          || window.getLivewireAssetPath('avatar-fallback.png')}
      />
    );
  }

  get wireTitle() {
    return <strong>{_.get(this.props.notification, 'wire.title', 'Unknown')}</strong>;
  }

  get stockCodeCompanyName() {
    return <strong>{_.get(this.props.notification, 'stock_code.company_name', 'A company you follow')}</strong>;
  }

  get fundName() {
    return <strong>{_.get(this.props.notification, 'fund.name', 'A fund you follow')}</strong>;
  }

  singularProfileActionMessage(action, target) {
    return (
      <p>
        <strong>{this.profileName}</strong>
        {` ${action} `}
        {target}
      </p>
    );
  }

  singularStockCodeActionMessage(action, target) {
    return (
      <p>
        <strong>{this.stockCodeCompanyName}</strong>
        {` ${action} `}
        {target}
      </p>
    );
  }

  singularFundActionMessage(action, target) {
    return (
      <p>
        <strong>{this.fundName}</strong>
        {` ${action} `}
        {target}
      </p>
    );
  }

  rollupProfilesActionMessage(action) {
    function name(p) {
      return <strong>{p.name}</strong>;
    }
    const profiles = this.props.notification.rolled_up_profiles;
    return (
      <p>
        {() => {
          const len = profiles.length;
          const third = len > 3 ? { name: `${len - 2} others` } : profiles[2];
          const list = _(profiles).take(3).fill(third, 2).map(name)
            .value();
          return (
            <React.Fragment>
              {_.compact([
                _.nth(list, -3),
                _.slice(list, -2).join(' and '),
              ]).join(', ') || 'Users have'}
            </React.Fragment>
          );
        }}
        {` ${action} `}
        {this.wireTitle}
      </p>
    );
  }

  renderLikedWireMessage() {
    const likeCount = _.get(this.props.notification, 'like.count', 0);

    return this.state.anonymousNotificationsEnabled ? (
      <p>
        {'Your wire '}
        {this.wireTitle}
        {' received a new like!'}
        {likeCount > 0 ? ` It now has ${likeCount} ${likeCount === 1 ? 'like' : 'likes'}.` : ''}
      </p>
    ) : this.singularProfileActionMessage('liked', this.wireTitle);
  }

  renderLikedCommentMessage() {
    const likeCount = _.get(this.props.notification, 'like.count', 0);

    return this.state.anonymousNotificationsEnabled ? (
      <p>
        {'Your comment on '}
        {this.wireTitle}
        {' received a new like!'}
        {likeCount > 0 ? ` It now has ${likeCount} ${likeCount === 1 ? 'like' : 'likes'}.` : ''}
      </p>
    ) : (
      <p>
        <strong>{this.profileName}</strong>
        {' liked your comment on '}
        {this.wireTitle}
        {` - your comment has ${likeCount} ${likeCount === 1 ? 'like' : 'likes'}`}
      </p>
    );
  }

  renderCommentMessage() {
    return this.singularProfileActionMessage('commented on', this.wireTitle);
  }

  renderReplyCommentMessage() {
    const wireTitle = _.get(this.props.notification, 'wire.title', 'Unknown');
    return (
      <p>
        Your comment on
        {' '}
        <strong>{ wireTitle }</strong>
        {' '}
        has a reply
      </p>
    );
  }

  renderContributorAlertMessage() {
    const followerCount = _.get(this.props.notification, 'follow.count', 0);

    return this.state.anonymousNotificationsEnabled ? (
      <p>
        {'You have a new follower!  '}
        {followerCount > 0 ? `You now have ${followerCount} ${followerCount === 1 ? 'follower' : 'followers'}.` : ''}
      </p>
    ) : this.singularProfileActionMessage('has followed', 'you');
  }

  renderAlertNewWireMessage() {
    return this.singularProfileActionMessage('published', this.wireTitle);
  }

  renderAlertNewCommentMessage() {
    return this.singularProfileActionMessage('published a comment on', this.wireTitle);
  }

  renderAlertCommentPublishedMessage() {
    const wireTitle = _.get(this.props.notification, 'wire.title', 'Unknown');
    return (
      <p>
        Your comment on
        {' '}
        <strong>{ wireTitle }</strong>
        {' '}
        has been published
      </p>
    );
  }

  renderContributorMentionedInWireMessage() {
    return this.singularProfileActionMessage('mentioned you in', this.wireTitle);
  }

  renderFollowedContributorMentionedInWireMessage() {
    return this.singularProfileActionMessage('mentioned in', this.wireTitle);
  }

  renderRollupLikedWireMessage() {
    return this.rollupProfilesActionMessage('liked');
  }

  renderRollupCommentMessage() {
    return this.rollupProfilesActionMessage('commented on');
  }

  renderStockCodeMentionedInWireMessage() {
    return this.singularStockCodeActionMessage('was tagged in', this.wireTitle);
  }

  renderFundMentionedInWireMessage() {
    return this.singularFundActionMessage('was mentioned in', this.wireTitle);
  }

  renderMessage() {
    const { type } = this.props.notification;
    const unknown = () => (
      <p>
        {'Unknown alert type: '}
        <strong>{type}</strong>
      </p>
    );
    const renderFunc = this[_.camelCase(`render_${type}_message`)] || unknown;

    return renderFunc.bind(this)();
  }

  render() {
    const notif = this.props.notification;

    return (
      <article
        className="notification"
        data-is-read={this.state.read}
        onClick={this.onClick}
      >
        {this.profilePicture}
        {this.renderMessage()}
        <time dateTime={notif.created_at}>{moment(notif.created_at).fromNow()}</time>
        <button
          type="button"
          className="toggle-notification-is-read"
          title={`Mark as ${this.state.read ? 'unread' : 'read'}`}
          onClick={this.onToggle}
        >
          <span />
        </button>
      </article>
    );
  }
}
